import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { SEO_GROUPS, resolveSeoLocation, seoSectionHref, seoSections } from "../seo-navigation";
import "./seo-navigation.css";

export function SeoSidebar({
  external,
  onNavigate,
}: {
  external: boolean;
  onNavigate: () => void;
}) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const projectMatch = /^\/projects\/([^/]+)\/seo\/?$/u.exec(location.pathname);
  const inSeo = Boolean(projectMatch) || location.pathname === "/seo";
  const projectId = projectMatch ? projectMatch[1]! : inSeo ? params.get("project") : null;
  let decodedProjectId = projectId;
  try {
    if (projectMatch && projectId) decodedProjectId = decodeURIComponent(projectId);
  } catch {
    /* An invalid URL remains an unavailable project, never a render error. */
  }
  const [disclosure, setDisclosure] = useState<{ key: string; open: boolean } | null>(null);
  const open = disclosure?.key === location.key ? disclosure.open : inSeo;
  const preview = params.get("view") === "client" && location.pathname === "/seo";
  const audience = external || preview;
  const sections = seoSections(audience);
  // Every sub-view highlights its section (e.g. Rankings → Google rank checks keeps Rankings).
  const activeSection = projectMatch ? resolveSeoLocation(params, audience).section : null;
  const groups = [null, ...SEO_GROUPS]
    .map((group) => ({ group, items: sections.filter((s) => s.group === group) }))
    .filter((entry) => entry.items.length > 0);
  // On the SEO home before a project is chosen, the pages have nothing to open yet.
  const awaitingProject = location.pathname === "/seo" && !decodedProjectId;
  const home = new URLSearchParams();
  if (decodedProjectId) home.set("project", decodedProjectId);
  if (preview) home.set("view", "client");
  return (
    <div className="seo-sidebar-tree">
      <div className={`seo-sidebar-parent${inSeo ? " active" : ""}`}>
        <Link
          to={`/seo${home.size ? `?${home}` : ""}`}
          onClick={onNavigate}
          className="seo-nav-entry"
          aria-current={location.pathname === "/seo" ? "page" : undefined}
        >
          <span>
            <svg
              className="seo-nav-symbol"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M4 20V12M10 20V8M16 20V4M3 7l6-4 6 1 6-3" />
            </svg>
            SEO
          </span>
        </Link>
        <button
          type="button"
          aria-label={open ? "Collapse SEO navigation" : "Expand SEO navigation"}
          aria-expanded={open}
          aria-controls="seo-navigation-links"
          onClick={() => setDisclosure({ key: location.key, open: !open })}
        >
          <svg
            className="disclosure-chevron"
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M4 6l4 4 4-4" />
          </svg>
        </button>
      </div>
      <ul
        id="seo-navigation-links"
        className="seo-sidebar-links"
        hidden={!open}
        aria-label="SEO pages"
      >
        {awaitingProject ? (
          <li className="seo-sidebar-hint" id="seo-sidebar-choose-project">
            Choose a project on this page to open these sections.
          </li>
        ) : null}
        {groups.map(({ group, items }) => (
          <li key={group ?? "start"} className={group ? undefined : "seo-sidebar-start"}>
            {group ? <p className="seo-sidebar-group">{group}</p> : null}
            <ul>
              {items.map((section) => (
                <li key={section.id}>
                  {awaitingProject ? (
                    <span
                      className="seo-sidebar-disabled"
                      aria-disabled="true"
                      aria-describedby="seo-sidebar-choose-project"
                    >
                      {section.label}
                    </span>
                  ) : (
                    <Link
                      to={seoSectionHref(decodedProjectId, section.id)}
                      onClick={onNavigate}
                      className={activeSection === section.id ? "active" : ""}
                      aria-current={activeSection === section.id ? "page" : undefined}
                    >
                      {section.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
